import * as THREE from 'three/webgpu';

/**
 * 正交相机到透视等效距离的换算输入。
 */
export interface ResolvePerspectiveEquivalentDistanceInput {
    /** 正交相机实例。 */
    orthographicCamera: THREE.OrthographicCamera;
    /** 参考透视垂直 FOV（度）。 */
    perspectiveFov: number;
}

/**
 * 透视相机到正交等效 zoom 的换算输入。
 */
export interface ResolveOrthographicEquivalentZoomInput {
    /** 透视相机实例。 */
    perspectiveCamera: THREE.PerspectiveCamera;
    /** 正交相机当前视锥体高度（未除以 zoom）。 */
    orthographicFrustumHeight: number;
    /** 观察目标点。 */
    target: THREE.Vector3;
}

/**
 * 相机投影距离换算器。
 *
 * 职责：
 * 1. 在正交 zoom/frustum 与透视几何距离之间做等价换算；
 * 2. 供投影切换、标准视图预览等跨投影语义对齐场景复用；
 * 3. 只负责数学换算，不直接修改 Viewer 或 CameraManager 状态。
 *
 * 设计说明：
 * 1. 正交模式下屏幕构图由 zoom 决定，几何距离可能被动态裁剪推远且不影响画面大小；
 * 2. 透视模式下构图完全依赖几何距离与 FOV，不能直接复用正交相机的 position-target 距离；
 * 3. 因此以“当前可见世界高度”作为跨投影的不变量进行换算。
 */
export class CameraProjectionDistanceConverter {
    /**
     * 从正交相机当前可见世界高度推导等效透视观察距离。
     *
     * 公式：
     * - visibleHeight = |top - bottom| / zoom
     * - distance = visibleHeight / (2 * tan(fov / 2))
     *
     * @param input 换算输入。
     * @returns 等效透视观察距离。
     */
    public static resolvePerspectiveEquivalentDistance(
        input: ResolvePerspectiveEquivalentDistanceInput,
    ): number {
        const zoom = Math.max(input.orthographicCamera.zoom, Number.EPSILON);
        const frustumHeight = Math.abs(input.orthographicCamera.top - input.orthographicCamera.bottom);
        const visibleHeight = frustumHeight / zoom;
        const halfFovRad = THREE.MathUtils.degToRad(input.perspectiveFov / 2);
        const halfTan = Math.max(Math.tan(halfFovRad), Number.EPSILON);

        return Math.max(visibleHeight / (2 * halfTan), Number.EPSILON);
    }

    /**
     * 从透视相机当前观察距离推导等效正交 zoom。
     *
     * 公式：
     * - visibleHeight = 2 * distance * tan(fov / 2)
     * - zoom = frustumHeight / visibleHeight
     *
     * @param input 换算输入。
     * @returns 等效正交 zoom。
     */
    public static resolveOrthographicEquivalentZoom(
        input: ResolveOrthographicEquivalentZoomInput,
    ): number {
        const distance = Math.max(
            input.perspectiveCamera.position.distanceTo(input.target),
            Number.EPSILON,
        );
        const halfFovRad = THREE.MathUtils.degToRad(input.perspectiveCamera.fov / 2);
        const visibleHeight = 2 * distance * Math.tan(halfFovRad);
        const frustumHeight = Math.max(input.orthographicFrustumHeight, Number.EPSILON);

        return Math.max(frustumHeight / Math.max(visibleHeight, Number.EPSILON), Number.EPSILON);
    }
}
